import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ordersPanel.scss";
import OrderCard from "../components/Orders/orderCard";
import ShirtlabDetails from "../components/Orders/shirtlabOrderDetails";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";

const SHIRTLAB_COLLECTION = "shirtLabOrders";
const PAGE_SIZE = 20;

const buildPaginationItems = (currentPage, totalPages, maxVisible = 7) => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages = [1];
  const middleSlots = maxVisible - 2;
  let start = Math.max(2, currentPage - Math.floor(middleSlots / 2));
  let end = start + middleSlots - 1;

  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = end - middleSlots + 1;
  }

  if (start > 2) pages.push("ellipsis-left");
  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    pages.push(pageNum);
  }
  if (end < totalPages - 1) pages.push("ellipsis-right");
  pages.push(totalPages);
  return pages;
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
};

let firebaseApp;
try {
  firebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
} catch (e) {
  console.error("[shirtlabPanel] Failed to init Firebase app", e);
}
const db = firebaseApp ? getFirestore(firebaseApp) : null;

const formatDateTime = (value) => {
  if (!value) return "—";
  const date =
    value instanceof Date
      ? value
      : (() => {
        try {
          return new Date(value);
        } catch (_) {
          return null;
        }
      })();
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const normalizeCollection = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry) => normalizeCollection(entry));
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeCollection(parsed);
    } catch (_) {
      return trimmed
        .split(/\r?\n|[,;]/)
        .map((part) => part.trim())
        .filter(Boolean);
    }
  }
  if (typeof value === "object") {
    return Object.entries(value).map(([key, val]) => {
      if (val === null || val === undefined || val === "") {
        return String(key);
      }
      if (typeof val === "object") {
        try {
          return `${key}: ${JSON.stringify(val)}`;
        } catch (_) {
          return `${key}: [object]`;
        }
      }
      return `${key}: ${val}`;
    });
  }
  return [String(value)];
};


const mapSupabaseOrder = (row) => {
  const createdRaw = row?.created_at ?? null;
  const createdAt =
    createdRaw && typeof createdRaw === "object" && typeof createdRaw.toDate === "function"
      ? createdRaw.toDate()
      : createdRaw
        ? new Date(createdRaw)
        : null;
  const firstName = row?.first_name ? String(row.first_name).trim() : "";
  const lastName = row?.last_name ? String(row.last_name).trim() : "";
  const company = row?.company ? String(row.company).trim() : "";
  const phone = row?.phone ? String(row.phone).trim() : "";
  const email = row?.email ? String(row.email).trim() : "";
  const items = normalizeCollection(row?.items);
  const designs = normalizeCollection(row?.designs);
  const status = row?.status ? String(row.status).trim() : "";
  const paymentStatus = row?.payment_status ? String(row.payment_status).trim() : "";
  const total = row?.order_total ? String(row.order_total).trim() : "";
  const orderNotes = row?.order_details ? String(row.order_details).trim() : "";
  const fallbackName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const customerName = company || fallbackName || "Customer";

  const searchText = [
    row?.id,
    customerName,
    firstName,
    lastName,
    company,
    phone,
    email,
    items.join(" "),
    designs.join(" "),
    orderNotes,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    id: row?.id ?? "",
    createdAt,
    createdAtLabel: formatDateTime(createdAt),
    firstName,
    lastName,
    company,
    phone,
    email,
    total,
    items,
    status,
    designs,
    paymentStatus,
    itemsCount: items.length,
    designsCount: designs.length,
    customerName,
    searchText,
    orderNotes,
    raw: row,
  };
};

export default function ShirtlabPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeOrder, setActiveOrder] = useState(null);
  const [page, setPage] = useState(1);
  const [tableSort, setTableSort] = useState({ field: 'date', direction: 'desc' });
  const [orderStatusUpdates, setOrderStatusUpdates] = useState({});
  useEffect(() => {
    console.log("Active order in ShirtlabPanel:", activeOrder);
  }, [activeOrder]);
  const loadOrders = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        if (!db) {
          throw new Error("Firebase is not configured for ShirtLab orders.");
        }

        const qRef = query(
          collection(db, SHIRTLAB_COLLECTION),
          orderBy("created_at", "desc")
        );
        const snapshot = await getDocs(qRef);
        const data = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const mapped = data.map(mapSupabaseOrder);
        setOrders(mapped);
        setOrderStatusUpdates(() => {
          const statusMap = {};
          mapped.forEach((order) => {
            statusMap[order.id] = order.status;
          });
          return statusMap;
        });
        setActiveOrder((prev) => {
          if (!prev) return prev;
          return mapped.find((order) => order.id === prev.id) || null;
        });
      } catch (err) {
        console.error("❌ Failed to fetch shirtlab orders:", err);
        setError(err);
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const filteredOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return orders;
    return orders.filter((order) => order.searchText.includes(query));
  }, [orders, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredOrders.slice(start, start + PAGE_SIZE);
  }, [filteredOrders, currentPage]);

  useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
  }, [currentPage, page]);

  const handleRefresh = () => {
    loadOrders({ silent: true });
  };

  const isLoading = loading && !refreshing;

  const handleHeaderSort = (field) => {
    setTableSort((prev) => {
      if (prev.field === field) {
        const nextDirection = prev.direction === 'asc' ? 'desc' : 'asc';
        return { field, direction: nextDirection };
      }
      const baseDirection = field === 'date' ? 'desc' : 'asc';
      return { field, direction: baseDirection };
    });
  };

  const handleStatusChange = useCallback(
    async (orderId, nextStatus) => {
      if (!orderId) return;
      const trimmedStatus = typeof nextStatus === "string" ? nextStatus.trim() : nextStatus;
      const previousStatus = orderStatusUpdates[orderId];

      setOrderStatusUpdates((prev) => ({
        ...prev,
        [orderId]: trimmedStatus,
      }));

      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId
            ? {
              ...order,
              status: trimmedStatus,
              raw: { ...order.raw, status: trimmedStatus },
            }
            : order
        )
      );

      setActiveOrder((prev) =>
        prev && prev.id === orderId
          ? {
            ...prev,
            status: trimmedStatus,
            raw: { ...prev.raw, status: trimmedStatus },
          }
          : prev
      );

      if (!db) {
        console.warn("Firebase is not configured; skipping remote status update.");
        return;
      }

      try {
        await updateDoc(doc(db, SHIRTLAB_COLLECTION, orderId), {
          status: trimmedStatus,
        });
        console.log(`✅ Firebase order ${orderId} status updated to ${trimmedStatus}.`);
      } catch (firebaseError) {
        console.error(`❌ Failed to update Firebase order ${orderId} status:`, firebaseError);
        setOrderStatusUpdates((prev) => ({
          ...prev,
          [orderId]: previousStatus,
        }));
        setOrders((prev) =>
          prev.map((order) =>
            order.id === orderId
              ? {
                ...order,
                status: previousStatus,
                raw: { ...order.raw, status: previousStatus },
              }
              : order
          )
        );
        setActiveOrder((prev) =>
          prev && prev.id === orderId
            ? {
              ...prev,
              status: previousStatus,
              raw: { ...prev.raw, status: previousStatus },
            }
            : prev
        );
        alert("Failed to update order status. Check console for details.");
      }
    },
    [orderStatusUpdates]
  );

  useEffect(() => {
    if (!activeOrder) return;
    const updatedStatus = orderStatusUpdates[activeOrder.id];
    if (updatedStatus && updatedStatus !== activeOrder.status) {
      setActiveOrder((prev) =>
        prev
          ? {
            ...prev,
            status: updatedStatus,
            raw: { ...prev.raw, status: updatedStatus },
          }
          : prev
      );
    }
  }, [activeOrder, orderStatusUpdates]);



  const renderSortableHeader = (field, label) => {
    const isActive = tableSort.field === field;
    const direction = isActive ? tableSort.direction : null;
    const icon = direction === 'asc' ? '▲' : direction === 'desc' ? '▼' : '↕';
    const textLabel = typeof label === 'string' ? label : String(label);
    const ariaLabel = isActive
      ? `${textLabel} sorted ${direction === 'asc' ? 'ascending' : 'descending'}`
      : `Sort by ${textLabel}`;
    return (
      <button
        type="button"
        onClick={() => handleHeaderSort(field)}
        className={`op-sortable${isActive ? ' is-active' : ''}`}
        aria-label={ariaLabel}
      >
        <span className="op-sortable__label">{label}</span>
        <span className="op-sortable__icon" aria-hidden="true">{icon}</span>
      </button>
    );
  };
  return (
    <div className="op-page">
      <div className="op-shell">
        <header className="op-header">
          <h1>Custom Apparel Orders</h1>

        </header>

        <div className="op-toolbar">
          <div className="op-toolbar__left">
            <button
              type="button"
              className="op-btn op-btn--ghost"
              onClick={handleRefresh}
              disabled={refreshing || isLoading}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <div className="op-search">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M10 4a6 6 0 014.8 9.6l4.8 4.8-1.4 1.4-4.8-4.8A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z"
                />
              </svg>
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search name, company, email, phone, items…"
              />
            </div>
          </div>

          <span style={{ color: "var(--op-muted)" }}>
            {filteredOrders.length} orders
          </span>
          {isLoading && (
            <span style={{ color: "var(--op-muted)" }}>Loading…</span>
          )}
        </div>
        {!activeOrder ? (
          <div className="op-table" role="table">
            <div className="op-head" role="row">
              <div className="op-cell op-cell--check">
                <input
                  type="checkbox"
                  aria-label="select all"
                />
              </div>
              <div className="op-cell op-cell--order">{renderSortableHeader('order', 'Order')}</div>
              <div className="op-cell op-cell--date">{renderSortableHeader('date', 'Date')}</div>
              <div className="op-cell op-cell--customer">{renderSortableHeader('customer', 'Customer')}</div>
              <div className="op-cell op-cell--total">{renderSortableHeader('total', 'Total')}</div>
              <div className="op-cell op-cell--items">{renderSortableHeader('items', 'Items')}</div>
              <div className="op-cell op-cell--fulfillment">{renderSortableHeader('fulfillment', 'Status')}</div>
              <div className="op-cell op-cell--payment">{renderSortableHeader('payment', 'Payment')}</div>
              <div className="op-cell op-cell--action">Action</div>
            </div>

            {paginatedOrders.map((order) => (

              <OrderCard key={order.id}
                order={order}
                onClick={() => setActiveOrder(order)}
              />
              // <div
              //   key={order.id || order.createdAtLabel}
              //   className="op-row"
              //   role="row"
              //   tabIndex={0}
              //   onClick={() => setActiveOrder(order)}
              //   onKeyDown={(e) => {
              //     if (e.key === "Enter" || e.key === " ") {
              //       e.preventDefault();
              //       setActiveOrder(order);
              //     }
              //   }}
              // >
              //   <div className="op-cell op-cell--order">
              //     {order.id ? `#${order.id}` : "—"}
              //   </div>
              //   <div className="op-cell op-cell--date">
              //     {order.createdAtLabel}
              //   </div>
              //   <div className="op-cell op-cell--customer">
              //     <div style={{ fontWeight: 600 }}>{order.customerName}</div>
              //     {(order.company && order.company !== order.customerName) ||
              //       order.firstName ||
              //       order.lastName ? (
              //       <div style={{ color: "var(--op-muted)", fontSize: 12 }}>
              //         {[order.firstName, order.lastName]
              //           .filter(Boolean)
              //           .join(" ")}
              //         {order.company && order.company !== order.customerName
              //           ? ` • ${order.company}`
              //           : ""}
              //       </div>
              //     ) : null}
              //   </div>
              //   <div className="op-cell op-cell--items">
              //     {order.itemsCount || "—"}
              //   </div>
              //   <div className="op-cell op-cell--items">
              //     {order.designsCount || "—"}
              //   </div>
              //   <div className="op-cell op-cell--total">
              //     {order.phone || "—"}
              //   </div>
              //   <div className="op-cell op-cell--payment">
              //     {order.email || "—"}
              //   </div>
              // </div>
            ))}


            {
              totalPages > 1 && (
                <div className="op-pagination">
                  {buildPaginationItems(currentPage, totalPages).map((item) => {
                    if (typeof item !== "number") {
                      return (
                        <span key={item} className="op-pagination__ellipsis">
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={item}
                        className={currentPage === item ? "active" : ""}
                        onClick={() => setPage(item)}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              )
            }
          </div>

        ) : (
          <>

            <div
              className="order-details-container"
              style={{ marginTop: "24px" }}
            >
              <ShirtlabDetails
                selectedOrder={activeOrder}
                onBack={() => setActiveOrder(null)}
                handleStatusChange={handleStatusChange}
                currentStatus={orderStatusUpdates[activeOrder.id]}
              />

            </div>

          </>
        )}
      </div>

      {error && (
        <div
          style={{
            border: "1px solid var(--op-border)",
            background: "#fef2f2",
            color: "#991b1b",
            padding: "12px",
            borderRadius: "10px",
            marginBottom: "16px",
          }}
        >
          Failed to load ShirtLab orders: {error.message || "Unknown error"}
        </div>
      )}



    </div>
  );
}
